import apiClient from './apiClient';
import { API_URL } from '../config';

export interface CPTExam {
  id: number;
  cpt_code: string;
  exam_name: string;
  exam_name_formal?: string | null;
  modality: string;
  body_region: string;
  subspecialty: string | null;
  laterality: string | null;
  contrast: string | null;
  description: string | null;
  report_template: string | null;
  synonyms?: string[];
  clinical_keywords?: string[];
  template_sections?: string[];
  validation_rules?: Record<string, unknown>[];
  is_active: boolean;
}

export interface CPTExamSearchResponse {
  items: CPTExam[];
  total: number;
}

export interface CPTDetectionResult {
  cpt_code: string;
  exam_name: string;
  modality: string;
  body_region: string;
  laterality: string | null;
  contrast: string | null;
  confidence: number;
  detection_method: string;
}

export interface CPTSearchParams {
  q?: string;
  modality?: string;
  body_region?: string;
  subspecialty?: string;
  limit?: number;
  offset?: number;
}

const searchExams = async (params: CPTSearchParams): Promise<CPTExamSearchResponse> => {
  const response = await apiClient.get(`${API_URL}/cpt/exams`, { params });
  return response.data;
};

const getExam = async (
  cptCode: string,
  bodyRegion?: string,
  examName?: string
): Promise<CPTExam> => {
  const params: Record<string, string> = {};
  if (bodyRegion) params.body_region = bodyRegion;
  if (examName) params.exam_name = examName;
  const response = await apiClient.get(`${API_URL}/cpt/exams/${cptCode}`, {
    params: Object.keys(params).length > 0 ? params : undefined,
  });
  return response.data;
};

const getModalities = async (): Promise<string[]> => {
  const response = await apiClient.get(`${API_URL}/cpt/modalities`);
  return response.data;
};

const getBodyRegions = async (): Promise<string[]> => {
  const response = await apiClient.get(`${API_URL}/cpt/body-regions`);
  return response.data;
};

const getSubspecialties = async (): Promise<string[]> => {
  const response = await apiClient.get(`${API_URL}/cpt/subspecialties`);
  return response.data;
};

const detectFromTranscript = async (
  transcript: string,
  useLlm = false,
  sessionId?: string
): Promise<CPTDetectionResult> => {
  const response = await apiClient.post(
    `${API_URL}/cpt/detect`,
    null,
    { params: { transcript, use_llm: useLlm, ...(sessionId ? { session_id: sessionId } : {}) } }
  );
  return response.data;
};

const cptExamService = {
  searchExams,
  getExam,
  getModalities,
  getBodyRegions,
  getSubspecialties,
  detectFromTranscript,
};

/** Values that should never appear in a CPT label. */
const _SKIP = new Set(['none', 'n/a', '', 'not applicable', 'bilateral']);

/**
 * Derive laterality from a CPT exam when the explicit `laterality` field is empty.
 * The CPT dataset encodes laterality in `exam_name` (e.g. "MR Knee Right")
 * but leaves the standalone `laterality` column as null.
 */
export function resolveLaterality(exam: CPTExam): string | null {
  if (exam.laterality && !_SKIP.has(exam.laterality.toLowerCase())) {
    return exam.laterality;
  }
  const lower = exam.exam_name.toLowerCase();
  if (/\bleft\b/.test(lower)) return 'left';
  if (/\bright\b/.test(lower)) return 'right';
  if (/\bbilateral\b/.test(lower)) return 'bilateral';
  return null;
}

/**
 * Build a human-readable CPT label like "70553 — MR Brain With And Without Contrast".
 * Appends laterality/contrast only when they are meaningful AND not already
 * embedded in the exam_name.
 */
export function buildCPTLabel(exam: CPTExam): string {
  if (!exam || !exam.exam_name) {
    return exam?.cpt_code ? `${exam.cpt_code} — Unknown Exam` : 'Unknown Exam';
  }
  const nameLower = exam.exam_name.toLowerCase();
  const extras: string[] = [];

  if (exam.laterality && !_SKIP.has(exam.laterality.toLowerCase())) {
    const latDisplay = exam.laterality.charAt(0).toUpperCase() + exam.laterality.slice(1);
    if (!nameLower.includes(exam.laterality.toLowerCase())) {
      extras.push(latDisplay);
    }
  }

  if (exam.contrast && !_SKIP.has(exam.contrast.toLowerCase())) {
    const contrastNorm = exam.contrast.replace(/_/g, ' ');
    if (!nameLower.includes(contrastNorm.toLowerCase())) {
      const contrastDisplay = contrastNorm.charAt(0).toUpperCase() + contrastNorm.slice(1);
      extras.push(contrastDisplay);
    }
  }

  const fullName = extras.length > 0
    ? `${exam.exam_name}, ${extras.join(', ')}`
    : exam.exam_name;

  return `${exam.cpt_code} \u2014 ${fullName}`;
}

export default cptExamService;
